/**
 * linkify.js
 *
 * Turns plain-text URLs inside an HTML string into clickable <a> elements
 * without disturbing any existing markup.
 *
 * Strategy (browser-DOM based, no dependencies):
 *   1. Parse the incoming HTML into a detached <div> so backend markup
 *      (<br>, <a>, entities, ...) is preserved exactly as the browser
 *      would render it.
 *   2. Walk only TEXT nodes with a TreeWalker, skipping anything already
 *      inside <a>, <script> or <style>, so URLs are never double-linkified
 *      and script/style bodies are never touched.
 *   3. Replace URL substrings inside those text nodes with <a> elements
 *      whose text and href are set through the DOM (never string
 *      concatenation), so user text can never inject markup.
 *   4. Serialize the detached <div> back to an HTML string.
 *
 * Falls back to returning the input untouched when there is no DOM
 * available (SSR / non-browser) or when the input is not a non-empty
 * string.
 */

// Matches http(s):// or bare www. URLs. Trailing punctuation and unbalanced
// closing brackets are cleaned up afterwards by trimTrailingChars, so the
// pattern itself can stay simple and greedy.
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"'`]+/gi

// Element tag names whose text content must never be linkified.
const SKIP_TAGS = { A: true, SCRIPT: true, STYLE: true }

// Characters that are never meaningful at the very end of a URL.
const TRAILING_PUNCTUATION = '.,;:!?"\'’”«»'

// Closing brackets mapped to their opening counterpart, used to keep
// balanced parens/brackets inside a URL while trimming unbalanced ones.
const CLOSING_TO_OPENING = { ')': '(', ']': '[', '}': '{' }

function countChar(text, char) {
  let count = 0
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === char) {
      count += 1
    }
  }
  return count
}

/**
 * Strips sentence punctuation and unbalanced closing brackets from the end
 * of a matched URL. "see https://x.com." keeps the URL without the ".", and
 * "https://en.wikipedia.org/wiki/Foo_(bar)" keeps its balanced ")".
 * @param {String} url
 * @return {String}
 */
function trimTrailingChars(url) {
  let end = url.length

  while (end > 0) {
    const char = url[end - 1]

    if (TRAILING_PUNCTUATION.indexOf(char) !== -1) {
      end -= 1
      continue
    }

    const opening = CLOSING_TO_OPENING[char]
    if (opening) {
      const candidate = url.slice(0, end)
      if (countChar(candidate, char) > countChar(candidate, opening)) {
        end -= 1
        continue
      }
    }

    break
  }

  return url.slice(0, end)
}

/**
 * Builds a safe href for a matched URL, or null when it is not an http(s)
 * link. Dangerous schemes such as javascript: / data: never reach here
 * because the regex only matches http(s)/www, but we double-check anyway.
 * @param {String} url
 * @return {String|null}
 */
function buildHref(url) {
  const href = /^www\./i.test(url) ? `https://${url}` : url
  return /^https?:\/\//i.test(href) ? href : null
}

/**
 * Turns a single text node's value into a DocumentFragment of text + <a>
 * nodes. Returns null when the node contains no linkifiable URL.
 * @param {Text} textNode
 * @param {Document} doc
 * @return {DocumentFragment|null}
 */
function buildFragment(textNode, doc) {
  const text = textNode.nodeValue
  URL_REGEX.lastIndex = 0

  let match = URL_REGEX.exec(text)
  if (!match) {
    return null
  }

  const fragment = doc.createDocumentFragment()
  let lastIndex = 0

  while (match) {
    const raw = match[0]
    const url = trimTrailingChars(raw)
    const href = buildHref(url)
    const start = match.index

    // Text between the previous match and this one.
    if (start > lastIndex) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex, start)))
    }

    if (href) {
      const anchor = doc.createElement('a')
      anchor.setAttribute('href', href)
      anchor.setAttribute('target', '_blank')
      anchor.setAttribute('rel', 'noopener noreferrer nofollow')
      // textContent (not innerHTML) => the label can never inject markup.
      anchor.textContent = url
      fragment.appendChild(anchor)
    } else {
      // Not a safe link: keep the (trimmed) match as plain text.
      fragment.appendChild(doc.createTextNode(url))
    }

    // Trailing punctuation we trimmed off stays as plain text.
    const trailing = raw.slice(url.length)
    if (trailing) {
      fragment.appendChild(doc.createTextNode(trailing))
    }

    lastIndex = start + raw.length
    match = URL_REGEX.exec(text)
  }

  // Remaining text after the last match.
  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)))
  }

  return fragment
}

/**
 * Makes plain-text URLs inside an HTML string clickable, leaving all other
 * markup intact and never double-linkifying existing anchors.
 * @param {String} html - Raw comment text/HTML from the backend.
 * @return {String} HTML with plain-text URLs wrapped in safe <a> elements.
 */
export function linkify(html) {
  // Empty / null / undefined / non-string: return unchanged, never throw.
  if (!html || typeof html !== 'string') {
    return html
  }

  // No DOM available (SSR / non-browser environment): no-op fallback.
  if (typeof document === 'undefined' || !document.createElement) {
    return html
  }

  const container = document.createElement('div')
  container.innerHTML = html

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) {
        return NodeFilter.FILTER_REJECT
      }
      let parent = node.parentNode
      while (parent && parent !== container) {
        if (SKIP_TAGS[parent.nodeName]) {
          return NodeFilter.FILTER_REJECT
        }
        parent = parent.parentNode
      }
      return NodeFilter.FILTER_ACCEPT
    },
  })

  // Collect first, mutate after: replacing nodes mid-walk breaks traversal.
  const textNodes = []
  let current = walker.nextNode()
  while (current) {
    textNodes.push(current)
    current = walker.nextNode()
  }

  textNodes.forEach((textNode) => {
    const fragment = buildFragment(textNode, document)
    if (fragment && textNode.parentNode) {
      textNode.parentNode.replaceChild(fragment, textNode)
    }
  })

  return container.innerHTML
}

export default linkify
